import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { createDraftLesson, getCourse, getLessons, getProgressForLessons, type Course, type Lesson } from "../lib/firebase";
import { checkCourseAccess } from "../lib/entitlement";

/**
 * Course landing - redirects into where the student left off (first lesson
 * not yet completed; first lesson when done/new). The full split-pane
 * learning UI lives in LessonPage.
 */
export function CoursePage() {
  const { courseId } = useParams<{ courseId: string }>();
  const { user, isAdmin, previewTier } = useAuth();
  const navigate = useNavigate();

  const [course, setCourse] = useState<Course | null>(null);
  const [lessons, setLessons] = useState<Lesson[] | null>(null);
  const [nextLessonId, setNextLessonId] = useState<string | null>(null);
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!courseId || !user) return;
    Promise.all([
      getCourse(courseId),
      getLessons(courseId),
      checkCourseAccess(user.uid, courseId, { isAdmin, previewTier }),
    ])
      .then(async ([c, l, decision]) => {
        const visible = l.filter((x) => x.published || isAdmin);
        const prog = await getProgressForLessons(user.uid, visible.map((x) => x.id));
        const next = visible.find((x) => !prog.get(x.id)?.completedAt) ?? visible[0];
        setCourse(c);
        setLessons(visible);
        setNextLessonId(next?.id ?? null);
        setAllowed(decision.granted);
      })
      .catch((e) => setError(e.message));
  }, [courseId, user, isAdmin, previewTier]);

  if (!courseId) return <Navigate to="/dashboard" replace />;
  if (error) return <main><p className="error">{error}</p></main>;
  if (allowed === false) return <Navigate to="/dashboard" replace />;
  if (!course || !lessons || allowed === null)
    return <p style={{ padding: "3rem", color: "var(--c-text-muted)" }}>กำลังโหลด…</p>;

  // Auto-advance to where the student left off.
  if (nextLessonId) return <Navigate to={`/courses/${courseId}/lessons/${nextLessonId}`} replace />;

  return (
    <main>
      <header className="page-header">
        <Link to="/dashboard" className="page-header__back">← Dashboard</Link>
        {isAdmin && <Link to={`/dashboard?edit=${courseId}`} className="btn-secondary">✏️ แก้ไขคอร์ส</Link>}
      </header>
      <span className="kicker">คอร์ส</span>
      <h1>{course.title}</h1>
      <p style={{ color: "var(--c-text-muted)", marginTop: "1rem", maxWidth: "65ch" }}>
        {course.description}
      </p>
      <div className="empty-state" style={{ marginTop: "2rem" }}>
        <h2>ยังไม่มีบทเรียน</h2>
        <p>กลับมาใหม่อีกครั้งเมื่อผู้สอนเพิ่มบทเรียนแรก</p>
        {isAdmin && (
          <button
            className="btn-primary"
            onClick={async () => navigate(`/courses/${courseId}/lessons/${await createDraftLesson(courseId)}?edit=1`)}
          >
            + เพิ่มบทแรก
          </button>
        )}
      </div>
    </main>
  );
}
